import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import WorkCard from "@/components/base/WorkCard";
import NewsCard from "@/components/base/NewsCard";
import HeroCarousel from "./components/HeroCarousel";
import SearchStrip from "./components/SearchStrip";
import usePageMeta from "@/hooks/usePageMeta";
import { heroSlides, homeStats, homeIntro } from "@/mocks/home";
import { works } from "@/mocks/works";
import { newsList } from "@/mocks/news";
import { profile } from "@/mocks/profile";

const featuredIds = [
  "night-train",
  "letters-of-the-tide",
  "white-corridor",
  "silent-testimony",
  "letters-from-kirishima",
  "last-chapter-of-summer",
];

export default function Home() {
  usePageMeta({
    title: "翔田千里－知名影视演员介绍与作品资讯平台",
    description:
      "翔田千里官方网站，提供翔田千里参与的影视作品介绍、最新动态和演员资料，涵盖电影电视剧信息，满足影迷多样化需求。",
    keywords: "翔田千里,影视演员,影视作品,电影介绍,电视剧资讯,演员资料,影视动态",
  });

  const featuredWorks = featuredIds
    .map((id) => works.find((work) => work.id === id))
    .filter((work): work is (typeof works)[number] => Boolean(work));

  const latestNews = [...newsList]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 3);

  return (
    <SiteLayout overlayHeader>
      <HeroCarousel slides={heroSlides} />
      <SearchStrip />

      <section className="bg-background-100 py-14 md:py-20">
        <div className="container-page">
          <SectionHeading
            eyebrow="Selected Works"
            title="精选影视作品"
            description="从悬疑电影到医疗剧集，挑选六部最能代表翔田千里表演轨迹的作品。点击封面可进入作品详情，查看剧情、剧照与相关信息。"
            actionLabel="查看全部 14 部作品"
            actionTo="/works"
          />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
            {featuredWorks.map((work, index) => (
              <Reveal key={work.id} delay={index * 60}>
                <WorkCard work={work} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-14 md:py-20">
        <div className="container-page grid grid-cols-1 gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-14">
          <Reveal>
            <div className="relative">
              <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-primary-900">
                <img
                  src={profile.portraitAlt}
                  alt="翔田千里在片场的工作照"
                  title="翔田千里 演员个人资料"
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                  <div>
                    <p className="font-heading text-lg text-background-50">{profile.name}</p>
                    <p className="mt-1 text-[11px] tracking-wide text-background-50/70">{profile.tagline}</p>
                  </div>
                  <Link
                    to="/profile"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-50/90 text-primary-800 transition-colors hover:bg-accent-400 hover:text-primary-950"
                    aria-label="查看演员个人资料"
                  >
                    <i className="ri-arrow-right-up-line" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
              <div className="absolute -bottom-5 -right-3 hidden rounded-lg bg-accent-500 px-4 py-3 text-primary-950 md:block">
                <p className="font-heading text-2xl leading-none">2010</p>
                <p className="mt-1 text-[10px] tracking-wide">出道至今</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div>
              <div className="flex items-center gap-2">
                <span className="h-px w-7 bg-accent-500"></span>
                <span className="text-[11px] font-medium uppercase tracking-wider2 text-accent-600">
                  Actor Profile
                </span>
              </div>
              <h2 className="mt-3 font-heading text-2xl leading-snug text-foreground-950 md:text-[30px]">
                一位把情绪留在镜头之外的演员
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">{homeIntro}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{profile.careerIntro}</p>

              <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {homeStats.map((stat) => (
                  <div key={stat.label} className="rounded-lg bg-background-100 px-3 py-4">
                    <p className="font-heading text-2xl text-primary-700">{stat.value}</p>
                    <p className="mt-1 text-[11px] text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/profile"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700"
                >
                  查看完整资料
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/80 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  关于本站
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="container-page">
          <SectionHeading
            eyebrow="Latest News"
            title="最新动态"
            description="访谈、活动报道与作品公告，按时间倒序更新。所有资讯均可点击进入详情页阅读。"
            actionLabel="全部动态资讯"
            actionTo="/news"
          />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
            {latestNews.map((item, index) => (
              <Reveal key={item.id} delay={index * 80}>
                <NewsCard item={item} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-14 md:py-20">
        <div className="container-page">
          <SectionHeading
            eyebrow="Approach"
            title="表演方法与创作态度"
            description="整理自近年访谈与映后交流，帮助理解她在作品中的选择。"
          />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
            {profile.approach.map((item, index) => (
              <Reveal key={item.title} delay={index * 80}>
                <div className="flex h-full flex-col rounded-lg border border-background-200/80 bg-background-100/60 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                    <i
                      className={
                        index === 0 ? "ri-eye-off-line" : index === 1 ? "ri-map-pin-time-line" : "ri-scissors-cut-line"
                      }
                      aria-hidden="true"
                    ></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-primary-950 py-16 md:py-20">
        <img
          src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20theatre%20stage%20with%20deep%20navy%20velvet%20curtains%20and%20warm%20amber%20spotlight%20cutting%20through%20soft%20haze%2C%20empty%20wooden%20floor%2C%20dramatic%20minimal%20composition%2C%20wide%20editorial%20photography%2C%20no%20people%2C%20no%20text&width=1600&height=600&seq=home-cta-stage&orientation=landscape"
          alt="剧场舞台光影"
          title="翔田千里 影视作品资料站"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/80 to-primary-950/50"></div>
        <div className="container-page relative">
          <div className="max-w-2xl">
            <span className="text-[11px] uppercase tracking-wider2 text-accent-300">Archive</span>
            <h2 className="mt-3 font-heading text-2xl leading-snug text-background-50 md:text-4xl">
              从一部作品开始，认识翔田千里
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-background-50/70">
              14 部影视作品、9 条动态资讯、一份完整的演员年表。无论你是第一次看到她的名字，还是已经看过她的全部作品，这里都能找到需要的信息。
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/works"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
              >
                浏览作品库
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
              <Link
                to="/news"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-6 py-3 text-sm text-background-50 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
              >
                阅读最新动态
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}